import { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';

import { SwipeableDrawer } from '@mui/material';
import {
    IosShareRounded,
    LinkRounded,
    PlayArrowRounded,
    ReportProblemOutlined,
    TuneOutlined,
} from '@mui/icons-material';

import PreviewReportIssueDrawer from './PreviewReportIssueDrawer';
import StatusToast from './StatusToast';
import PreviewOptionRow from './PreviewOptionRow';
import { eventPreviewShared } from './eventHelper';
import { shareContent, SHARE_FAILURE_MESSAGE } from '../utils/shareContent';
import { LABEL_DOMAIN, PREVIEW_PREFERENCE_COPY_TITLE } from '../constants/labels';

const previewShareParts = (preview) => {
    const trackId = preview?.fullTrackId || preview?.trackId || '';
    const messageIndex = preview?.messageIndex ?? '';
    return {
        trackId,
        messageIndex,
        url: `https://${LABEL_DOMAIN}/preview/${encodeURIComponent(trackId)}/${encodeURIComponent(messageIndex)}`,
    };
};

const PreviewInfoOptionsDrawer = ({
    open,
    onClose,
    preview,
    authenticated,
    disableShareLink = false,
    disableFullEpisode = false,
    onFullEpisodeClick,
    onReportClick,
    onMoreOrLessClick,
}) => {
    const navigate = useNavigate();
    const analytics = getAnalytics();
    const [copiedToastGeneration, setCopiedToastGeneration] = useState(0);
    const [shareError, setShareError] = useState('');
    const [showReportDrawer, setShowReportDrawer] = useState(false);

    const { trackId, messageIndex, url } = previewShareParts(preview);
    const canShare = !disableShareLink && typeof navigator.share === 'function';
    const shareLabel = disableShareLink ? 'Copy Link' : 'Share';

    const closeCopied = () => {
        setCopiedToastGeneration(0);
    };

    const sharePreview = async () => {
        setShareError('');
        setCopiedToastGeneration(0);
        logEvent(analytics, 'share_preview_card');
        try {
            const result = await shareContent({ url }, { copyOnly: disableShareLink });
            if (result === 'cancelled') return;
            if (result === 'copied') setCopiedToastGeneration(generation => generation + 1);
            eventPreviewShared(`${trackId}_${messageIndex}`);
            onClose();
        } catch (_) {
            setShareError(SHARE_FAILURE_MESSAGE);
        }
    };

    const playEpisode = () => {
        onClose();
        if (onFullEpisodeClick) {
            onFullEpisodeClick(preview);
            return;
        }
        if (trackId) {
            navigate(`/track/${encodeURIComponent(trackId)}?start=0`);
        }
    };

    const reportIssue = () => {
        onClose();
        if (onReportClick) {
            onReportClick(preview);
            return;
        }
        setShowReportDrawer(true);
    };

    return (
        <>
            <StatusToast
                open={copiedToastGeneration > 0}
                instanceKey={copiedToastGeneration}
                onClose={closeCopied}
                message='Copied to clipboard'
            />
            <StatusToast tone='error' open={Boolean(shareError)} message={shareError} onClose={() => setShareError('')} />

            <SwipeableDrawer
                anchor='bottom'
                open={open}
                onClose={onClose}
                onOpen={() => {}}
                disableDiscovery
                ModalProps={{ keepMounted: false }}
                PaperProps={{
                    sx: {
                        borderRadius: '18px 18px 0px 0px',
                    },
                }}
            >
                <div className='previewInfoOptionsDrawer appDrawerSurface'>
                    <div className='previewPreferenceDragIndicator' />
                    <div className='previewInfoOptionsRows'>
                        {authenticated && onMoreOrLessClick && (
                            <PreviewOptionRow
                                Icon={TuneOutlined}
                                title={PREVIEW_PREFERENCE_COPY_TITLE}
                                onClick={() => {
                                    onClose();
                                    onMoreOrLessClick();
                                }}
                            />
                        )}
                        <PreviewOptionRow
                            Icon={canShare ? IosShareRounded : LinkRounded}
                            title={shareLabel}
                            onClick={sharePreview}
                        />

                        {authenticated && (
                            <PreviewOptionRow
                                Icon={ReportProblemOutlined}
                                title='Report issue'
                                onClick={reportIssue}
                            />
                        )}

                        <PreviewOptionRow
                            Icon={PlayArrowRounded}
                            title='Play episode'
                            disabled={disableFullEpisode}
                            onClick={playEpisode}
                        />
                    </div>
                </div>
            </SwipeableDrawer>

            <PreviewReportIssueDrawer
                open={showReportDrawer}
                onClose={() => setShowReportDrawer(false)}
                preview={preview}
            />
        </>
    );
};

export default PreviewInfoOptionsDrawer;
